import { ProductSlug } from "@/api/product-api";
import { CurrencyIsoCode, GameResell } from "@/models/GameResell";
import {
  EssentialGameItem,
  GameItem,
  GameItemState,
} from "@/models/orders/OrderModelV2";
import { CartItemWithOptionalName } from "@/store/cart-store/type";
import { GameUtil } from "@/utils/GameUtil";

export class CartUtil {
  /** 글러브 디바이스(사이즈 세트/번들 제외) — DK2/DK3 본품만. */
  static isGloveDevice(slug: ProductSlug): boolean {
    return (
      slug === ProductSlug.TACTGLOVE_DK2 || slug === ProductSlug.TACTGLOVE_DK3
    );
  }

  /** 글러브 디바이스 → 자기 사이즈 세트(unlisted bundle) slug. 비글러브는 null. */
  static getGloveBundleSlug(slug: ProductSlug): ProductSlug | null {
    if (slug === ProductSlug.TACTGLOVE_DK2)
      return ProductSlug.TACTGLOVE_DK2_BUNDLE;
    if (slug === ProductSlug.TACTGLOVE_DK3)
      return ProductSlug.TACTGLOVE_DK3_BUNDLE;
    return null;
  }

  static getIncreasedItems(
    previous: CartItemWithOptionalName[],
    current: CartItemWithOptionalName[],
  ): CartItemWithOptionalName[] {
    const previousQuantities = new Map(
      previous.map((item) => [item.sku, item.quantity]),
    );

    return current
      .filter((item) => {
        const prevQuantity = previousQuantities.get(item.sku) ?? 0;
        return item.quantity > prevQuantity;
      })
      .map((item) => ({
        ...item,
        quantity: item.quantity - (previousQuantities.get(item.sku) ?? 0),
      }));
  }

  static getDecreasedItems(
    previous: CartItemWithOptionalName[],
    current: CartItemWithOptionalName[],
  ): CartItemWithOptionalName[] {
    const currentQuantities = new Map(
      current.map((item) => [item.sku, item.quantity]),
    );

    return previous
      .filter((item) => {
        const currQuantity = currentQuantities.get(item.sku) ?? 0;
        return item.quantity > currQuantity;
      })
      .map((item) => ({
        ...item,
        quantity: item.quantity - (currentQuantities.get(item.sku) ?? 0),
      }));
  }

  static getGameItems(
    gameResells: GameResell[] | undefined,
    gameItems: EssentialGameItem[],
    currency: CurrencyIsoCode,
  ): GameItem[] {
    if (!gameResells || gameItems.length === 0) return [];
    return gameItems
      .map((item) => {
        const gameResell = gameResells.find(
          (game) => game.id === item.gameResellId,
        );
        if (!gameResell) {
          return null;
        }

        return this.getGameItem({
          gameResell,
          platform: item.platform,
          currency,
        });
      })
      .filter((item) => !!item) as GameItem[];
  }

  static getGameItem({
    gameResell,
    platform,
    currency,
  }: {
    gameResell: GameResell;
    platform: string;
    currency: CurrencyIsoCode;
  }): GameItem | null {
    const platformItem = (gameResell.platformList || []).find(
      (p) => p.platform === platform,
    );

    if (!platformItem) {
      return null;
    }

    const price = GameUtil.findPrice(platformItem.priceList, currency);

    // ★통화에 가격이 없으면 **null 을 반환한다** — 0 으로 떨어뜨리지 않는다(#5179 리뷰 §4).
    //
    // 종전엔 `price?.price ?? 0` 이라 미등록 통화에서 **가격 0 짜리 게임 아이템**이 만들어졌고,
    // `getGameItems` 의 `.filter(!!item)` 은 객체가 존재하므로 그대로 통과시켰다. 그 결과
    // 게임 키가 €0 으로 카트에 담기고 자체 결제로 그대로 프로비저닝된다. `price <= 0` 가드는
    // GE 경로(`get-checkout-cart-info`) 한 곳에만 있어 다른 경로를 전혀 막지 못했다.
    //
    // null 로 바꾸면 이미 존재하는 null 필터를 **모든 호출처가 상속**한다 — 게임이 목록·카트에서
    // 빠지지, 공짜로 팔리지 않는다. 미등록 통화(EU/GB)의 근본 해결은 mgmt 게임 카탈로그에
    // EUR/GBP 등록(admin#1442/#1443).
    //
    // ★**가격이 0 으로 등록돼 있어도** 같다 — 행은 있는데 `price: 0` 이면 종전엔 가격 0 게임 아이템이
    // 만들어져 자체 결제(KR·JP 등 비-GE)로 **공짜 게임 키**가 나갔다(#5886). 막는 건 **정가(`price`) 0**
    // 하나다 — 정가가 있고 100% 할인된 무료 게임(`discount === price`)은 의도된 무료라 통과시킨다.
    // 판정은 `GameUtil.findPrice`(통화 일치 ∧ 정가 > 0) 한 곳 — 제품 쪽 `ProductUtil.getPrice` 와 같은 기준.
    // GE cart-info 는 이 null 을 그대로 쓴다.
    if (!price) {
      return null;
    }

    const discountPercentile = price.discountPercentile;

    const compatibleDevices = gameResell.compatibleDevices;
    return {
      state: GameItemState.Prepared,
      platform,
      discountPercentile: discountPercentile ?? 0,
      compatibleDevices,
      price: price.price,
      discount: price.discount,
      name: gameResell.name,
      id: gameResell.contentId,
      gameResellId: gameResell.id,
      thumbnail: gameResell.thumbnailPortraitImageLink,
      thumbnailHorizontal: gameResell.thumbnailImageLink,
      disableCountryList: gameResell.disableCountryList,
    };
  }
}
